import React, { useState } from 'react';
import { ArrowLeft, Users, Briefcase, Dog, Mail, Phone, Calendar, Clock, MapPin, ArrowRight } from 'lucide-react';
import { format, isValid, parseISO } from 'date-fns';
import type { Location, Stop, BookingDetails } from '../types';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

interface JetCategory {
  id: string;
  name: string;
  description: string;
  priceRange: string;
  capacity: string;
  range: string;
  speed: string;
  imageUrl: string;
}

interface BookingRequestFormProps {
  origin: Location;
  destination: Location;
  stops: Stop[];
  selectedDate: string;
  selectedTime: string;
  isReturn: boolean;
  bookingDetails: BookingDetails;
  selectedCategory: JetCategory;
  onBack: () => void;
}

export default function BookingRequestForm({
  origin,
  destination,
  stops,
  selectedDate,
  selectedTime,
  isReturn,
  bookingDetails,
  selectedCategory,
  onBack
}: BookingRequestFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [distance, setDistance] = useState<number>(0);

  // Calculate distance between origin and destination
  React.useEffect(() => {
    if (origin && destination) {
      try {
        const R = 6371; // Earth's radius in km
        const dLat = (destination.lat - origin.lat) * Math.PI / 180;
        const dLon = (destination.lng - origin.lng) * Math.PI / 180;
        const a = 
          Math.sin(dLat/2) * Math.sin(dLat/2) +
          Math.cos(origin.lat * Math.PI / 180) * Math.cos(destination.lat * Math.PI / 180) * 
          Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        const calculatedDistance = R * c;
        setDistance(calculatedDistance);
      } catch (error) {
        console.error('Error calculating distance:', error);
        setDistance(0);
      }
    }
  }, [origin, destination]);

  const formatLocation = (location: Location) => {
    if (!location?.address) return { city: '', code: '' };
    const parts = location.address.split('(');
    return {
      city: parts[0].trim(),
      code: parts[1]?.replace(')', '') || ''
    };
  };

  const formatDateTime = (date: string, time: string) => {
    try {
      if (!date || !time) return 'Not specified';
      
      // Create a proper ISO date string
      const dateTimeString = `${date}T${time}:00`;
      const dateObj = parseISO(dateTimeString);
      
      // Check if the date is valid
      if (!isValid(dateObj)) {
        return 'Invalid date/time';
      }
      
      return format(dateObj, 'MMM d, yyyy h:mm a');
    } catch (error) {
      console.error('Error formatting date/time:', error);
      return 'Invalid date/time';
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhoneChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      phone: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Simulate sending email
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // In a real application, you would send this data to your backend
      console.log('Sending booking request to bookings@privatecharterx.com', {
        formData,
        flightDetails: {
          origin: formatLocation(origin).city,
          destination: formatLocation(destination).city,
          departureDate: formatDateTime(selectedDate, selectedTime),
          stops: stops.map(stop => formatLocation(stop)),
          isReturn,
          passengers: bookingDetails.passengers,
          luggage: bookingDetails.luggage,
          pets: bookingDetails.pets,
          category: selectedCategory.name,
          distance: Math.round(distance)
        }
      });
      
      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-100 px-6 py-4">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <div>
            <h2 className="text-xl font-bold">Booking Request</h2>
            <div className="text-sm text-gray-600 mt-1 flex items-center gap-2">
              <span>{formatLocation(origin).city}</span>
              <ArrowRight size={14} className="text-gray-400" />
              <span>{formatLocation(destination).city}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6 space-y-6">
          {isSubmitted ? (
            <div className="bg-green-50 text-green-700 p-6 rounded-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-lg">Request Sent Successfully!</h3>
                  <p>Thank you for your booking request. Our team will contact you shortly to finalize the details.</p>
                </div>
              </div>
              <p className="text-sm">
                A confirmation email has been sent to {formData.email}. If you have any questions, please contact us at bookings@privatecharterx.com or call +41 44 797 88 53.
              </p>
            </div>
          ) : (
            <>
              {/* Route Summary */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1">
                    <div className="text-xl font-bold">{formatLocation(origin).city}</div>
                    <div className="text-sm text-gray-500">{formatLocation(origin).code}</div>
                  </div>
                  <ArrowRight size={20} className="hidden md:block text-gray-400" />
                  <div className="flex-1 md:text-right">
                    <div className="text-xl font-bold">{formatLocation(destination).city}</div>
                    <div className="text-sm text-gray-500">{formatLocation(destination).code}</div>
                  </div>
                </div>

                {stops.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <div className="text-sm font-medium text-gray-700">Stops</div>
                    {stops.map((stop, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 p-2 rounded-lg">
                        <MapPin size={16} className="text-gray-400" />
                        <span>{formatLocation(stop).city}</span>
                        <span className="text-gray-400">•</span>
                        <span>{formatDateTime(stop.date, stop.time)}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500">Departure</div>
                    <div className="font-medium">{formatDateTime(selectedDate, selectedTime)}</div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-500">Total Distance</div>
                    <div className="font-medium">{Math.round(distance)} km</div>
                  </div>
                </div>
              </div>

              {/* Aircraft Category */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-4">
                  <img
                    src={selectedCategory.imageUrl}
                    alt={selectedCategory.name}
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                  <div>
                    <h4 className="font-bold">{selectedCategory.name}</h4>
                    <div className="text-sm text-gray-500">{selectedCategory.description}</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded-full">{selectedCategory.capacity}</span>
                      <span className="text-xs px-2 py-1 bg-green-50 text-green-700 rounded-full">{selectedCategory.priceRange}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Passenger Details */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <div className="flex items-center gap-2 text-gray-500 mb-1">
                      <Users size={16} />
                      <span>Passengers</span>
                    </div>
                    <div className="font-bold">{bookingDetails.passengers}</div>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 text-gray-500 mb-1">
                      <Briefcase size={16} />
                      <span>Luggage</span>
                    </div>
                    <div className="font-bold">{bookingDetails.luggage}</div>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 text-gray-500 mb-1">
                      <Dog size={16} />
                      <span>Pets</span>
                    </div>
                    <div className="font-bold">{bookingDetails.pets}</div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="Enter your full name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="Enter your email address"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <div className="relative">
                    <PhoneInput
                      country={'ch'}
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      inputProps={{
                        name: 'phone',
                        required: true,
                        className: 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent'
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Additional Information
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent resize-none"
                    placeholder="Please provide any additional details or special requests..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-black text-white py-3 rounded-lg hover:bg-gray-800 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Sending Request...</span>
                    </>
                  ) : (
                    <>
                      <Mail size={18} />
                      <span>Send Request</span>
                    </>
                  )}
                </button>

                <div className="text-center text-sm text-gray-500">
                  Your request will be sent to bookings@privatecharterx.com
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}